import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Input } from '@structural-climbing/ui'
import { ProductTile } from './ProductTile'
import type { Cart, Product, ProductCategory } from './types'

interface Props {
  categories: ProductCategory[]
  products: Product[]
  currency: string
  activeCart: Cart | null
  onAdd: (product: Product) => void
  /** Shown above the search, and pinned with it while the products scroll. */
  toolbar?: React.ReactNode
}

const ALL = 'all'

export function ProductGrid({ categories, products, currency, activeCart, onAdd, toolbar }: Props) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [categoryId, setCategoryId] = useState<string>(ALL)

  // Only what can be sold. An archived product never shows on the sell screen.
  const onSale = useMemo(() => products.filter((p) => p.is_active), [products])
  // A category with nothing on sale would be a filter that always shows nothing.
  const usedCategories = useMemo(
    () => categories.filter((c) => onSale.some((p) => p.category_id === c.id)),
    [categories, onSale],
  )

  const needle = search.trim().toLowerCase()
  const visible = onSale.filter(
    (p) =>
      (categoryId === ALL || p.category_id === categoryId) &&
      (!needle || p.name.toLowerCase().includes(needle)),
  )

  const inCart = new Map(activeCart?.items.map((line) => [line.product_id, line.quantity]))

  return (
    <div className="min-w-0 space-y-3">
      {/* Pinned while the products scroll. On a phone it sits under the
          layout's 57px menu bar, tucked 1px under it so no gap shows. */}
      <div className="sticky top-[56px] z-20 -mx-2 space-y-3 bg-background px-2 pt-3 pb-3 md:top-0">
        {toolbar}
        <Input
          size="sm"
          isClearable
          aria-label={t('products.searchProducts')}
          placeholder={t('products.searchProducts')}
          value={search}
          onValueChange={setSearch}
          onClear={() => setSearch('')}
        />

        {usedCategories.length > 1 && (
          <div className="scrollbar-hide flex gap-2 overflow-x-auto">
            {[{ id: ALL, name: t('products.allCategories') }, ...usedCategories].map((category) => (
              <Button
                key={category.id}
                size="sm"
                radius="full"
                className="shrink-0"
                color={categoryId === category.id ? 'primary' : 'default'}
                variant={categoryId === category.id ? 'solid' : 'flat'}
                aria-pressed={categoryId === category.id}
                onPress={() => setCategoryId(category.id)}
              >
                {category.name}
              </Button>
            ))}
          </div>
        )}
      </div>

      {visible.length === 0 ? (
        <p className="py-10 text-center text-sm text-foreground/50">{t('products.noMatches')}</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
          {visible.map((product) => (
            <ProductTile
              key={product.id}
              product={product}
              currency={currency}
              quantityInCart={inCart.get(product.id) ?? 0}
              onAdd={onAdd}
            />
          ))}
        </div>
      )}
    </div>
  )
}
